import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { FormEvent, ReactElement, ReactNode, useState } from 'react';
import { ButtonWithLoader } from '../../design/buttons/ButtonWithLoader';
import { S_Modal } from '../../design/modals/modal/S_Modal';
import { InputStatus } from '../models/error.model';
import { ValidatedTextInput } from './ValidatedTextInput';

interface IProps {
  confirmationDescription?: string;
  confirmationQuestion: string;
  confirmationHeader: string;
  confirmationTextHeader?: ReactNode;
  confirmationText: string;
  confirmationYes?: string;
  confirmationNo?: string;
  showSpinner?: boolean;
  showModal: boolean;
  loaderKey?: string;
  onConfirm(): Promise<void>;
  onCancel(): void;
}

export const ConfirmationModalWithTextBox = observer((props: IProps): ReactElement => {
  const [confirmationStatus, setConfirmationStatus] = useState(InputStatus.empty());
  const [confirmationText, setConfirmationText] = useState('');
  const { t } = useTranslator();

  const handleOnConfirm = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validateConfirmationText(confirmationText)) {
      return;
    }

    setConfirmationStatus(InputStatus.empty());
    setConfirmationText('');
    await props.onConfirm();
  };

  const handleOnCancel = (): void => {
    setConfirmationStatus(InputStatus.empty());
    setConfirmationText('');
    props.onCancel();
  };

  const handleChange = (text: string): void => {
    setConfirmationText(text);
    validateConfirmationText(text);
  };

  const validateConfirmationText = (value: string): boolean => {
    const status = InputStatus.valid();
    status.isValid = props.confirmationText === value;
    setConfirmationStatus(status);
    return status.isValid;
  };

  const isConfirmDisabled = !confirmationText || !confirmationStatus.isValid;

  return (
    <S_Modal
      isOpen={props.showModal}
      toggle={handleOnCancel}
      headerText={props.confirmationHeader}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={handleOnCancel}
          >
            {props.confirmationNo || t('cancel')}
          </Button>
          <ButtonWithLoader
            onClick={(e) => {
              void handleOnConfirm(e);
            }}
            disabled={isConfirmDisabled}
            loading={props.showSpinner}
            loaderKey={props.loaderKey}
          >
            {props.confirmationYes || t('yes')}
          </ButtonWithLoader>
        </>
      }
    >
      {props.confirmationDescription && <div>{props.confirmationDescription}</div>}
      <div>{props.confirmationQuestion}</div>
      {props.confirmationTextHeader && <div>{props.confirmationTextHeader}</div>}
      <ValidatedTextInput
        id='confirmation-input'
        type='text'
        maxLength={200}
        value={confirmationText}
        placeholder={props.confirmationText}
        onChange={handleChange}
        isValid={confirmationStatus.isValid}
        feedbacks={confirmationStatus.errorCodes}
      />
    </S_Modal>
  );
});
